import React, { useState, useEffect } from "react";
import { Tab, Tabs, Modal, Badge } from "react-bootstrap";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import GMBProfileEditor from "../components/GMBProfileEditor";
import { server_post_data } from "../ServiceConnection/serviceconnection";
import {
  get_gmb_locations,
  get_gmb_location_details,
  get_gmb_reviews,
  reply_gmb_review,
  delete_gmb_review_reply,
  get_gmb_insights,
  get_gmb_search_keywords,
  get_gmb_posts,
  create_gmb_post,
  update_gmb_post,
  delete_gmb_post,
  get_gmb_media,
  upload_gmb_media,
  delete_gmb_media,
  get_gmb_competitors,
} from "../ServiceConnection/serviceconnection";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";

import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";

/* -------------------------------------------------------------------- */
/* Generic helper to call any GMB endpoint with the standard "~@~" reply */
/* -------------------------------------------------------------------- */
const callGmbApi = async (url, extraParams = {}) => {
  let formdata = new FormData();
  Object.keys(extraParams).forEach((key) =>
    formdata.append(key, extraParams[key]),
  );

  const response = await server_post_data(url, formdata);

  // axios auto-parses the JsonResponse body into an object:
  // { message: "0~@~{...}", error: false }
  if (!response || typeof response.message !== "string") {
    handleError("Unexpected server response");
    return null;
  }

  const parts = response.message.split("~@~");
  const parsed = JSON.parse(parts[1]);
  if (parsed.error) {
    handleError(parsed.message || "Something went wrong");
    return null;
  }
  return parsed;
};

const STAR_MAP = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

/* Rating -> semantic status color, pulled from the root design tokens
   (danger -> warning -> success) instead of a bespoke palette */
const RATING_COLOR = {
  1: "var(--danger-main)",
  2: "var(--danger-main)",
  3: "var(--warning-main)",
  4: "var(--info-main)",
  5: "var(--success-main)",
};

const StarRow = ({ rating }) => {
  const count = STAR_MAP[rating] || 0;
  const pct = (count / 5) * 100;
  const color = RATING_COLOR[count] || "var(--neutral-400)";
  return (
    <div className="gmb-rating">
      <span className="gmb-rating-stars" style={{ color }}>
        {"★".repeat(count)}
        <span className="gmb-rating-stars-empty">{"★".repeat(5 - count)}</span>
      </span>
      <span className="gmb-rating-bar">
        <span
          className="gmb-rating-bar-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </span>
    </div>
  );
};

/* Numeric star row for Places API ratings (floats like 4.3, not word enums) */
const NumericStarRow = ({ rating }) => {
  const value = Number(rating) || 0;
  const pct = Math.min(Math.max((value / 5) * 100, 0), 100);
  const color =
    value >= 4.5
      ? "var(--success-main)"
      : value >= 3.5
        ? "var(--info-main)"
        : value >= 2.5
          ? "var(--warning-main)"
          : "var(--danger-main)";
  return (
    <span className="gmb-competitor-rating">
      <strong className="gmb-mono">{value ? value.toFixed(1) : "—"}</strong>
      <span className="gmb-rating-bar">
        <span
          className="gmb-rating-bar-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </span>
    </span>
  );
};

/* -------------------------------------------------------------------- */
/* Insights metric metadata — maps Google's raw enum keys to friendly    */
/* labels, icons, and a color used for the card accent + sparkline bars  */
/* -------------------------------------------------------------------- */
const METRIC_META = {
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: {
    label: "Desktop Search Views",
    icon: "🖥️",
    color: "var(--primary-600)",
  },
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS: {
    label: "Desktop Maps Views",
    icon: "🗺️",
    color: "var(--lilac-600)",
  },
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH: {
    label: "Mobile Search Views",
    icon: "📱",
    color: "var(--primary-700)",
  },
  BUSINESS_IMPRESSIONS_MOBILE_MAPS: {
    label: "Mobile Maps Views",
    icon: "📍",
    color: "var(--info-main)",
  },
  CALL_CLICKS: {
    label: "Call Clicks",
    icon: "📞",
    color: "var(--success-main)",
  },
  WEBSITE_CLICKS: {
    label: "Website Clicks",
    icon: "🌐",
    color: "var(--warning-main)",
  },
  BUSINESS_DIRECTION_REQUESTS: {
    label: "Direction Requests",
    icon: "🧭",
    color: "var(--danger-main)",
  },
  BUSINESS_CONVERSATIONS: {
    label: "Messages Started",
    icon: "💬",
    color: "var(--lilac-700)",
  },
  BUSINESS_BOOKINGS: {
    label: "Bookings",
    icon: "📅",
    color: "var(--lilac-800)",
  },
  BUSINESS_FOOD_ORDERS: {
    label: "Food Orders",
    icon: "🍽️",
    color: "var(--warning-hover)",
  },
};

/* -------------------------------------------------------------------- */
/* One metric card: total + a tiny bar-chart (sparkline) across the      */
/* selected date range, with a hover tooltip on each bar                 */
/* -------------------------------------------------------------------- */
const MetricCard = ({ metricKey, points }) => {
  const meta = METRIC_META[metricKey] || {
    label: metricKey,
    icon: "📊",
    color: "var(--neutral-500)",
  };
  // Google returns "value" as a string (int64-safe JSON encoding) — must
  // coerce to Number, otherwise 0 + "7" + "17" concatenates instead of adding.
  const values = points.map((p) => Number(p.value) || 0);
  const total = values.reduce((a, b) => a + b, 0);
  const max = Math.max(...values, 1);

  return (
    <div className="gmb-card gmb-metric-card">
      <div
        className="gmb-metric-icon"
        style={{ background: "var(--primary-50)", color: meta.color }}
      >
        {meta.icon}
      </div>
      <p className="gmb-metric-label">{meta.label}</p>
      <h4 className="gmb-metric-total gmb-mono">{total.toLocaleString()}</h4>

      <div className="d-flex align-items-end gap-2" style={{ height: 40 }}>
        {points.map((p, i) => {
          const val = Number(p.value) || 0;
          return (
            <div
              key={i}
              title={`${p.date?.month}/${p.date?.day}: ${val}`}
              style={{
                flex: 1,
                minWidth: 2,
                height: `${Math.max((val / max) * 100, 4)}%`,
                backgroundColor: meta.color,
                borderRadius: 2,
                opacity: val ? 1 : 0.15,
              }}
            />
          );
        })}
      </div>

      <div className="gmb-metric-range">
        <span>
          {points[0]?.date
            ? `${points[0].date.month}/${points[0].date.day}`
            : ""}
        </span>
        <span>
          {points[points.length - 1]?.date
            ? `${points[points.length - 1].date.month}/${points[points.length - 1].date.day}`
            : ""}
        </span>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------- */
/* Search keyword row — small horizontal bar sized against the highest   */
/* impression count in the list                                          */
/* -------------------------------------------------------------------- */
const KeywordRow = ({ keywordData, max }) => {
  const term =
    keywordData.searchKeyword || keywordData.searchKeywordText || "—";
  const count =
    Number(
      keywordData.insightsValue?.value ??
        keywordData.insightsValue?.threshold ??
        0,
    ) || 0;
  const pct = Math.max((count / max) * 100, 3);
  return (
    <div className="gmb-keyword-row">
      <span className="gmb-keyword-term">{term}</span>
      <span className="gmb-keyword-bar">
        <span className="gmb-keyword-bar-fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="gmb-keyword-count gmb-mono">
        {count ? count.toLocaleString() : "< 15"}
      </span>
    </div>
  );
};

/* -------------------------------------------------------------------- */
/* Competitor status badge — maps Places API businessStatus enum to a    */
/* small pill using the same semantic colors as the rest of the app      */
/* -------------------------------------------------------------------- */
const CompetitorStatusBadge = ({ status }) => {
  if (!status) return null;
  const map = {
    OPERATIONAL: { label: "Open", cls: "operational" },
    CLOSED_TEMPORARILY: { label: "Temp. closed", cls: "closed" },
    CLOSED_PERMANENTLY: { label: "Closed", cls: "closed" },
  };
  const entry = map[status] || { label: status, cls: "unknown" };
  return (
    <span className={`gmb-competitor-status ${entry.cls}`}>{entry.label}</span>
  );
};

/* -------------------------------------------------------------------- */
/* Per-competitor "vs you" badge — flags whether this specific competitor */
/* is ahead of or behind your own average rating, so a rating alone      */
/* doesn't have to be mentally compared card by card                     */
/* -------------------------------------------------------------------- */
const CompetitorDeltaBadge = ({ competitorRating, ownAvgRating }) => {
  if (competitorRating == null || ownAvgRating == null) return null;
  const diff = Number(competitorRating) - ownAvgRating;
  const rounded = Math.abs(diff).toFixed(1);
  if (Math.abs(diff) < 0.05) {
    return <span className="gmb-competitor-delta even">Tied with you</span>;
  }
  if (diff > 0) {
    return (
      <span className="gmb-competitor-delta ahead">
        ▲ {rounded} ahead of you
      </span>
    );
  }
  return (
    <span className="gmb-competitor-delta behind">▼ {rounded} behind you</span>
  );
};

/* -------------------------------------------------------------------- */
/* "You vs the area" summary — the whole point of the Competitors tab:   */
/* not just a list of nearby businesses, but where you stand against     */
/* them and what to actually do about it                                 */
/* -------------------------------------------------------------------- */
const CompetitorInsightSummary = ({
  ownAvgRating,
  ownReviewCount,
  areaAvgRating,
  areaAvgReviewCount,
  competitorCount,
}) => {
  if (!competitorCount) return null;

  const ratingKnown = ownAvgRating != null && areaAvgRating != null;
  const ratingDiff = ratingKnown ? ownAvgRating - areaAvgRating : null;
  const reviewDiff =
    areaAvgReviewCount != null ? ownReviewCount - areaAvgReviewCount : null;

  let takeaway;
  if (!ratingKnown) {
    takeaway =
      "Open the Reviews tab at least once so we have your own rating on file — then this comparison can tell you exactly where you stand.";
  } else if (ratingDiff >= 0.2) {
    takeaway = `You're rated ${ratingDiff.toFixed(1)} higher than the ${competitorCount} nearby businesses for this search — keep replying to every review to protect that lead.`;
  } else if (ratingDiff <= -0.2) {
    takeaway = `You're rated ${Math.abs(ratingDiff).toFixed(1)} lower than nearby competitors on average — check recent reviews for a recurring complaint you can fix, and reply publicly to every one.`;
  } else {
    takeaway =
      "Your rating is about even with the area average — review volume and how fast you reply to reviews are usually what tips a close race like this.";
  }

  if (reviewDiff != null && reviewDiff < -5) {
    takeaway += ` Also, nearby competitors average ${Math.round(areaAvgReviewCount)} reviews versus your ${ownReviewCount} — more reviews improve your ranking, so it's worth asking recent customers for one.`;
  }

  return (
    <div className="gmb-card gmb-competitor-insight mb-16">
      <p className="gmb-section-title mb-2">You vs. the area</p>
      <div className="gmb-competitor-insight-stats">
        <div className="gmb-competitor-insight-stat">
          <span className="gmb-competitor-insight-label">Your rating</span>
          <span className="gmb-competitor-insight-value gmb-mono">
            {ownAvgRating != null ? ownAvgRating.toFixed(1) : "—"}
          </span>
        </div>
        <div className="gmb-competitor-insight-stat">
          <span className="gmb-competitor-insight-label">Area average</span>
          <span className="gmb-competitor-insight-value gmb-mono">
            {areaAvgRating != null ? areaAvgRating.toFixed(1) : "—"}
          </span>
        </div>
        <div className="gmb-competitor-insight-stat">
          <span className="gmb-competitor-insight-label">Your reviews</span>
          <span className="gmb-competitor-insight-value gmb-mono">
            {ownReviewCount || "—"}
          </span>
        </div>
        <div className="gmb-competitor-insight-stat">
          <span className="gmb-competitor-insight-label">
            Competitor avg. reviews
          </span>
          <span className="gmb-competitor-insight-value gmb-mono">
            {areaAvgReviewCount != null ? Math.round(areaAvgReviewCount) : "—"}
          </span>
        </div>
      </div>
      <p className="gmb-competitor-insight-takeaway">{takeaway}</p>
    </div>
  );
};

const TAB_META = {
  locations: { label: "Locations", icon: "📍" },
  reviews: { label: "Reviews", icon: "★" },
  insights: { label: "Insights", icon: "📈" },
  posts: { label: "Posts", icon: "📝" },
  media: { label: "Media", icon: "🖼️" },
  competitors: { label: "Competitors", icon: "👑" },
};

const GMBDashboard = () => {
  const [activeTab, setActiveTab] = useState("locations");
  const [loading, setLoading] = useState(false);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);

  const [locations, setLocations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [insights, setInsights] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [posts, setPosts] = useState([]);
  const [media, setMedia] = useState({});
  const [questions, setQuestions] = useState([]);

  // ---- Competitors (Royal Premium) ----
  const [competitorKeyword, setCompetitorKeyword] = useState("");
  const [competitorRadius, setCompetitorRadius] = useState(5000);
  const [competitors, setCompetitors] = useState([]);
  const [competitorSearched, setCompetitorSearched] = useState(false);

  const [locationDetailModal, setLocationDetailModal] = useState({
    show: false,
    detail: null,
  });

  const [replyModal, setReplyModal] = useState({
    show: false,
    review: null,
    text: "",
  });

  const [postModal, setPostModal] = useState({
    show: false,
    mode: "create", // "create" | "edit"
    postId: null,
    postType: "STANDARD", // STANDARD | EVENT | OFFER
    summary: "",
    title: "",
    imageUrl: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    ctaType: "LEARN_MORE",
    ctaUrl: "",
    couponCode: "",
    redeemUrl: "",
    termsConditions: "",
  });

  const [uploadModal, setUploadModal] = useState({
    show: false,
    file: null,
    previewUrl: "",
    category: "ADDITIONAL",
    mediaFormat: "PHOTO",
  });

  const [answerModal, setAnswerModal] = useState({
    show: false,
    question: null,
    text: "",
  });

  const today = new Date();
  const priorMonth = new Date();
  priorMonth.setDate(today.getDate() - 30);
  const fmt = (d) => d.toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState({
    start: fmt(priorMonth),
    end: fmt(today),
  });

  /* ---------------------------- loaders ---------------------------- */

  const loadLocations = async () => {
    setLoading(true);
    const res = await callGmbApi(get_gmb_locations);
    if (res) setLocations(res.locations || []);
    setLoading(false);
  };

  const loadReviews = async () => {
    setLoading(true);
    const res = await callGmbApi(get_gmb_reviews);
    if (res) setReviews(res.reviews || []);
    setLoading(false);
  };

  const loadInsights = async () => {
    setLoading(true);
    const [insightsRes, keywordsRes] = await Promise.all([
      callGmbApi(get_gmb_insights, {
        start_date: dateRange.start,
        end_date: dateRange.end,
      }),
      callGmbApi(get_gmb_search_keywords),
    ]);
    if (insightsRes) setInsights(insightsRes.insights);
    if (keywordsRes) setKeywords(keywordsRes.keywords || []);
    setLoading(false);
  };

  const loadPosts = async () => {
    setLoading(true);
    const res = await callGmbApi(get_gmb_posts);
    if (res) setPosts(res.posts || []);
    setLoading(false);
  };

  const loadMedia = async () => {
    setLoading(true);
    const res = await callGmbApi(get_gmb_media);
    if (res) setMedia(res.mediaByCategory || {});
    setLoading(false);
  };

  // Competitors are search-driven (need a keyword), so this is NOT auto-run
  // when the tab opens — the user types a keyword and hits Search.
  // Own reviews are fetched alongside (if not already loaded) purely to
  // compute the "you vs area" comparison — it doesn't switch the active tab.
  const loadCompetitors = async () => {
    if (!competitorKeyword.trim()) {
      handleError('Enter a keyword first, e.g. "cafe" or "dentist"');
      return;
    }
    setLoading(true);
    const needOwnReviews = reviews.length === 0;
    const [competitorRes, ownReviewsRes] = await Promise.all([
      callGmbApi(get_gmb_competitors, {
        keyword: competitorKeyword.trim(),
        radius_meters: competitorRadius,
      }),
      needOwnReviews ? callGmbApi(get_gmb_reviews) : Promise.resolve(null),
    ]);
    setCompetitorSearched(true);
    if (competitorRes) setCompetitors(competitorRes.competitors || []);
    if (ownReviewsRes) setReviews(ownReviewsRes.reviews || []);
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === "locations") loadLocations();
    if (activeTab === "reviews") loadReviews();
    if (activeTab === "insights") loadInsights();
    if (activeTab === "posts") loadPosts();
    if (activeTab === "media") loadMedia();
    // competitors: intentionally not auto-loaded, see loadCompetitors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ---------------------------- location actions ---------------------------- */

  const viewLocationDetails = async () => {
    setLoading(true);
    const res = await callGmbApi(get_gmb_location_details);
    setLoading(false);
    if (res) setLocationDetailModal({ show: true, detail: res.location });
  };

  /* ---------------------------- review actions ---------------------------- */

  const submitReply = async () => {
    const res = await callGmbApi(reply_gmb_review, {
      reviewId: replyModal.review.reviewId,
      replyText: replyModal.text,
      platform: "Google",
    });
    if (res) {
      handleSuccess("Reply posted successfully");
      setReplyModal({ show: false, review: null, text: "" });
      loadReviews();
    }
  };

  const removeReply = async (reviewId) => {
    if (!window.confirm("Delete this reply?")) return;
    const res = await callGmbApi(delete_gmb_review_reply, {
      review_id: reviewId,
    });
    if (res) {
      handleSuccess("Reply deleted");
      loadReviews();
    }
  };

  /* ---------------------------- post actions ---------------------------- */

  const RESET_POST_MODAL = {
    show: true,
    mode: "create",
    postId: null,
    postType: "STANDARD",
    summary: "",
    title: "",
    imageUrl: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    ctaType: "LEARN_MORE",
    ctaUrl: "",
    couponCode: "",
    redeemUrl: "",
    termsConditions: "",
  };

  const openCreatePost = () => setPostModal({ ...RESET_POST_MODAL });

  const dateFromGoogle = (d) =>
    d
      ? `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`
      : "";

  const timeFromGoogle = (t) =>
    t
      ? `${String(t.hours || 0).padStart(2, "0")}:${String(t.minutes || 0).padStart(2, "0")}`
      : "";

  const openEditPost = (post) =>
    setPostModal({
      show: true,
      mode: "edit",
      postId: post.name?.split("/").pop(),
      postType: post.topicType || "STANDARD",
      summary: post.summary || "",
      title: post.event?.title || "",
      imageUrl: post.media?.[0]?.sourceUrl || post.media?.[0]?.googleUrl || "",
      startDate: dateFromGoogle(post.event?.schedule?.startDate),
      startTime: timeFromGoogle(post.event?.schedule?.startTime),
      endDate: dateFromGoogle(post.event?.schedule?.endDate),
      endTime: timeFromGoogle(post.event?.schedule?.endTime),
      ctaType: post.callToAction?.actionType || "LEARN_MORE",
      ctaUrl: post.callToAction?.url || "",
      couponCode: post.offer?.couponCode || "",
      redeemUrl: post.offer?.redeemOnlineUrl || "",
      termsConditions: post.offer?.termsConditions || "",
    });

  const submitPost = async () => {
    if (!postModal.summary.trim()) {
      handleError("Description is required");
      return;
    }
    if (postModal.postType !== "STANDARD" && !postModal.title.trim()) {
      handleError("Title is required for Event and Offer posts");
      return;
    }
    if (
      postModal.postType !== "STANDARD" &&
      (!postModal.startDate || !postModal.endDate)
    ) {
      handleError("Start date and end date are required");
      return;
    }

    const commonParams = {
      summary: postModal.summary,
      image_url: postModal.imageUrl,
      title: postModal.title,
      start_date: postModal.startDate,
      start_time: postModal.startTime,
      end_date: postModal.endDate,
      end_time: postModal.endTime,
      cta_type: postModal.ctaType,
      cta_url: postModal.ctaUrl,
      coupon_code: postModal.couponCode,
      redeem_url: postModal.redeemUrl,
      terms_conditions: postModal.termsConditions,
    };

    if (postModal.mode === "edit") {
      const res = await callGmbApi(update_gmb_post, {
        post_id: postModal.postId,
        ...commonParams,
      });
      if (res) {
        handleSuccess("Post updated successfully");
        setPostModal({ ...postModal, show: false });
        loadPosts();
      }
    } else {
      const res = await callGmbApi(create_gmb_post, {
        post_type: postModal.postType,
        ...commonParams,
      });
      if (res) {
        handleSuccess("Post created successfully");
        setPostModal({ ...postModal, show: false });
        loadPosts();
      }
    }
  };

  const removePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    const res = await callGmbApi(delete_gmb_post, { post_id: postId });
    if (res) {
      handleSuccess("Post deleted");
      loadPosts();
    }
  };

  /* ---------------------------- media actions ---------------------------- */

  const onPickMediaFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadModal((m) => ({
      ...m,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
  };

  const submitUpload = async () => {
    if (!uploadModal.file) {
      handleError("Please choose an image or video first");
      return;
    }
    const res = await callGmbApi(upload_gmb_media, {
      media_file: uploadModal.file,
      category: uploadModal.category,
      media_format: uploadModal.mediaFormat,
    });
    if (res) {
      handleSuccess("Media uploaded successfully");
      setUploadModal({
        show: false,
        file: null,
        previewUrl: "",
        category: "ADDITIONAL",
        mediaFormat: "PHOTO",
      });
      loadMedia();
    }
  };

  const removeMedia = async (mediaName) => {
    if (!mediaName) {
      handleError("Can't delete this item — missing media reference");
      return;
    }
    if (!window.confirm("Delete this media item?")) return;
    const res = await callGmbApi(delete_gmb_media, { media_name: mediaName });
    if (res) {
      handleSuccess("Media deleted");
      loadMedia();
    }
  };
  const copyMediaLink = async (url) => {
    if (!url) {
      handleError("No link available for this item");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      handleSuccess("Image link copied");
    } catch {
      handleError("Couldn't copy automatically — link: " + url);
    }
  };

  /* ---------------------------- render ---------------------------- */

  const maxKeywordCount = Math.max(
    ...keywords.map(
      (k) =>
        Number(k.insightsValue?.value ?? k.insightsValue?.threshold ?? 0) || 0,
    ),
    1,
  );

  // ---- "You vs area" comparison, used by the Competitors tab ----
  const ownRatedReviews = reviews
    .map((r) => STAR_MAP[r.starRating])
    .filter(Boolean);
  const ownAvgRating = ownRatedReviews.length
    ? ownRatedReviews.reduce((a, b) => a + b, 0) / ownRatedReviews.length
    : null;
  const ownReviewCount = reviews.length;

  const ratedCompetitors = competitors.filter((c) => c.rating != null);
  const areaAvgRating = ratedCompetitors.length
    ? ratedCompetitors.reduce((a, c) => a + Number(c.rating), 0) /
      ratedCompetitors.length
    : null;
  const competitorsWithReviewCount = competitors.filter(
    (c) => c.reviewCount != null,
  );
  const areaAvgReviewCount = competitorsWithReviewCount.length
    ? competitorsWithReviewCount.reduce((a, c) => a + c.reviewCount, 0) /
      competitorsWithReviewCount.length
    : null;

  return (
    <MasterLayout>
      {loading && <Loader />}
      <div className="gmb-premium">
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5>GMB Manager</h5>
            <span className="gmb-live">
              <span className="gmb-live-dot" />
              Connected
            </span>
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-sm btn-outline-primary-600"
              onClick={viewLocationDetails}
            >
              View Location Details
            </button>
            <button
              className="btn btn-sm btn-primary-600"
              onClick={() => setProfileEditorOpen(true)}
            >
              Edit Profile
            </button>
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-24"
        >
          {/* ---------------- LOCATIONS ---------------- */}
          <Tab
            eventKey="locations"
            title={
              <>
                <span>{TAB_META.locations.icon}</span>{" "}
                {TAB_META.locations.label}
              </>
            }
          >
            <p className="gmb-tab-sub">
              All Google Business Profile locations linked to this account.
            </p>
            <div className="row gy-4">
              {locations.length === 0 && !loading && (
                <div className="gmb-empty">No locations found yet.</div>
              )}
              {locations.map((loc, idx) => (
                <div className="col-md-6 col-lg-4" key={idx}>
                  <div className="gmb-card gmb-location-card">
                    <h6>{loc.title}</h6>
                    <p className="gmb-location-meta">
                      📍
                      {loc.storefrontAddress
                        ? [
                            ...(loc.storefrontAddress.addressLines || []),
                            loc.storefrontAddress.locality,
                            loc.storefrontAddress.administrativeArea,
                          ]
                            .filter(Boolean)
                            .join(", ")
                        : "No address"}
                    </p>
                    {loc.phoneNumbers && (
                      <p className="gmb-location-meta">
                        📞 {loc.phoneNumbers.primaryPhone}
                      </p>
                    )}
                    {loc.websiteUri && (
                      <a href={loc.websiteUri} target="_blank" rel="noreferrer">
                        {loc.websiteUri}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Tab>

          {/* ---------------- REVIEWS ---------------- */}
          <Tab
            eventKey="reviews"
            title={
              <>
                <span>{TAB_META.reviews.icon}</span> {TAB_META.reviews.label}
              </>
            }
          >
            <div className="d-flex flex-column gap-16">
              {reviews.length === 0 && !loading && (
                <div className="gmb-empty">No reviews found yet.</div>
              )}
              {reviews.map((rev, idx) => (
                <div className="gmb-card gmb-review-card" key={idx}>
                  <div className="gmb-review-head">
                    <div>
                      <h6 className="gmb-review-author">
                        {rev.reviewer?.displayName || "Anonymous"}
                      </h6>
                      <StarRow rating={rev.starRating} />
                      <p className="gmb-review-comment">{rev.comment}</p>
                    </div>
                    {!rev.reviewReply && (
                      <button
                        className="btn btn-sm btn-primary-600"
                        onClick={() =>
                          setReplyModal({ show: true, review: rev, text: "" })
                        }
                      >
                        Reply
                      </button>
                    )}
                  </div>
                  {rev.reviewReply && (
                    <div className="gmb-review-reply">
                      <p className="mb-0">
                        <span className="gmb-review-reply-label">
                          Your reply
                        </span>
                        {rev.reviewReply.comment}
                      </p>
                      <button
                        className="btn btn-sm btn-outline-danger-600"
                        onClick={() => removeReply(rev.reviewId)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Tab>

          {/* ---------------- INSIGHTS ---------------- */}
          <Tab
            eventKey="insights"
            title={
              <>
                <span>{TAB_META.insights.icon}</span> {TAB_META.insights.label}
              </>
            }
          >
            <div className="gmb-date-row">
              <div>
                <label>From</label>
                <input
                  type="date"
                  className="form-control"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                />
              </div>
              <div>
                <label>To</label>
                <input
                  type="date"
                  className="form-control"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                />
              </div>
              <button className="btn btn-primary-600" onClick={loadInsights}>
                Apply
              </button>
            </div>

            {insights?.multiDailyMetricTimeSeries && (
              <div className="row gy-4 mb-24">
                {insights.multiDailyMetricTimeSeries.flatMap((metricGroup) =>
                  (metricGroup.dailyMetricTimeSeries || []).map(
                    (series, sidx) => (
                      <div
                        className="col-md-6 col-lg-4"
                        key={`${series.dailyMetric}-${sidx}`}
                      >
                        <MetricCard
                          metricKey={series.dailyMetric}
                          points={series.timeSeries?.datedValues || []}
                        />
                      </div>
                    ),
                  ),
                )}
              </div>
            )}

            {!insights && !loading && (
              <div className="gmb-empty">
                No insights data yet — pick a date range and click Apply.
              </div>
            )}

            {keywords.length > 0 && (
              <div className="gmb-card p-20">
                <p className="gmb-section-title mb-2">
                  What people search to find you
                </p>
                <p className="gmb-tab-sub">
                  Top search terms this month (Google hides exact counts under
                  ~15 as "&lt; 15" for privacy).
                </p>
                <div className="d-flex flex-column gap-8">
                  {keywords.slice(0, 15).map((k, idx) => (
                    <KeywordRow
                      key={idx}
                      keywordData={k}
                      max={maxKeywordCount}
                    />
                  ))}
                </div>
              </div>
            )}
          </Tab>

          {/* ---------------- POSTS ---------------- */}
          <Tab
            eventKey="posts"
            title={
              <>
                <span>{TAB_META.posts.icon}</span> {TAB_META.posts.label}
              </>
            }
          >
            <button
              className="btn btn-primary-600 mb-16"
              onClick={openCreatePost}
            >
              + New Post
            </button>
            <div className="row gy-4">
              {posts.length === 0 && !loading && (
                <div className="gmb-empty">No posts found yet.</div>
              )}
              {posts.map((post, idx) => {
                const postImage =
                  post.media?.[0]?.googleUrl || post.media?.[0]?.sourceUrl;
                return (
                  <div className="col-md-6 col-lg-4" key={idx}>
                    <div className="gmb-card gmb-post-card">
                      {postImage && (
                        <img
                          src={postImage}
                          alt="Post"
                          className="gmb-post-image"
                          referrerPolicy="no-referrer"
                        />
                      )}
                      <span
                        className={`gmb-post-badge ${(post.topicType || "standard").toLowerCase()}`}
                      >
                        {post.topicType}
                      </span>
                      <p className="gmb-post-summary">{post.summary}</p>
                      <div className="d-flex gap-8">
                        <button
                          className="btn btn-sm btn-outline-primary-600"
                          onClick={() => openEditPost(post)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger-600"
                          onClick={() =>
                            removePost(post.name?.split("/").pop())
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Tab>

          {/* ---------------- MEDIA ---------------- */}
          <Tab
            eventKey="media"
            title={
              <>
                <span>{TAB_META.media.icon}</span> {TAB_META.media.label}
              </>
            }
          >
            <button
              className="btn btn-primary-600 mb-16"
              onClick={() =>
                setUploadModal({
                  show: true,
                  file: null,
                  previewUrl: "",
                  category: "ADDITIONAL",
                  mediaFormat: "PHOTO",
                })
              }
            >
              + Upload Media
            </button>
            {Object.keys(media).length === 0 && !loading && (
              <div className="gmb-empty">No media found yet.</div>
            )}
            {Object.entries(media).map(([category, items]) => (
              <div key={category} className="mb-24">
                <p className="gmb-media-group-title">{category}</p>
                <div className="row gy-3">
                  {items.map((item, idx) => (
                    <div className="col-md-3 col-6" key={idx}>
                      <div className="gmb-media-tile">
                        <img
                          src={item.thumbnailUrl || item.googleUrl}
                          alt={category}
                          referrerPolicy="no-referrer"
                        />
                        <div className="gmb-media-actions">
                          <button
                            className="gmb-media-action-btn"
                            title="Copy image link"
                            onClick={() => copyMediaLink(item.googleUrl)}
                          >
                            🔗
                          </button>
                          <button
                            className="gmb-media-action-btn danger"
                            title="Delete"
                            onClick={() => removeMedia(item.name)}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Tab>

          {/* ---------------- COMPETITORS (Royal Premium) ---------------- */}
          <Tab
            eventKey="competitors"
            title={
              <>
                <span>{TAB_META.competitors.icon}</span>{" "}
                {TAB_META.competitors.label}
              </>
            }
          >
            <p className="gmb-tab-sub">
              Enter a keyword for your business category — e.g. "cafe",
              "dentist", "electrician" — to see nearby competitors along with
              their rating, reviews, and distance from you.
            </p>

            <div className="gmb-competitor-search">
              <div>
                <label>Keyword</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. cafe, dentist, plumber"
                  value={competitorKeyword}
                  onChange={(e) => setCompetitorKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadCompetitors()}
                />
              </div>
              <div>
                <label>Radius</label>
                <select
                  className="form-control"
                  value={competitorRadius}
                  onChange={(e) => setCompetitorRadius(Number(e.target.value))}
                >
                  <option value={1000}>1 km</option>
                  <option value={3000}>3 km</option>
                  <option value={5000}>5 km</option>
                  <option value={10000}>10 km</option>
                  <option value={25000}>25 km</option>
                </select>
              </div>
              <button className="btn btn-primary-600" onClick={loadCompetitors}>
                🔍 Find Competitors
              </button>
            </div>

            <CompetitorInsightSummary
              ownAvgRating={ownAvgRating}
              ownReviewCount={ownReviewCount}
              areaAvgRating={areaAvgRating}
              areaAvgReviewCount={areaAvgReviewCount}
              competitorCount={competitors.length}
            />

            <div className="row gy-4">
              {competitorSearched && competitors.length === 0 && !loading && (
                <div className="gmb-empty">
                  No competitors found for this keyword and radius. Try
                  increasing the radius or searching a different keyword.
                </div>
              )}
              {!competitorSearched && !loading && (
                <div className="gmb-empty">
                  Enter a keyword and hit "Find Competitors" — nearby businesses
                  will show up here with their rating and distance.
                </div>
              )}
              {competitors.map((c, idx) => (
                <div className="col-md-6 col-lg-4" key={c.placeId || idx}>
                  <div className="gmb-card gmb-competitor-card">
                    <div className="d-flex justify-content-between align-items-start gap-8">
                      <h6 className="gmb-competitor-name mb-0">{c.name}</h6>
                      <CompetitorStatusBadge status={c.status} />
                    </div>
                    <p className="gmb-competitor-address mb-0">
                      📍 {c.address || "Address not available"}
                    </p>
                    <NumericStarRow rating={c.rating} />
                    <CompetitorDeltaBadge
                      competitorRating={c.rating}
                      ownAvgRating={ownAvgRating}
                    />
                    <div className="gmb-competitor-meta-row">
                      <span className="gmb-competitor-distance">
                        {c.distanceKm != null
                          ? `${c.distanceKm} km away`
                          : "Distance unknown"}
                      </span>
                      {c.mapsUrl && (
                        <a
                          className="gmb-competitor-maps-link"
                          href={c.mapsUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View on Maps →
                        </a>
                      )}
                    </div>
                    {c.reviewCount != null && (
                      <p className="gmb-competitor-address mb-0">
                        {c.reviewCount.toLocaleString()} reviews
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Tab>
        </Tabs>
      </div>

      {/* ---------------- Location Details Modal ---------------- */}
      <Modal
        show={locationDetailModal.show}
        onHide={() => setLocationDetailModal({ show: false, detail: null })}
        centered
        size="lg"
        contentClassName="gmb-modal-content"
        className="gmb-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>Location Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {locationDetailModal.detail && (
            <div>
              <div className="gmb-detail-row">
                <strong>Title</strong>
                <span>{locationDetailModal.detail.title}</span>
              </div>
              {locationDetailModal.detail.phoneNumbers && (
                <div className="gmb-detail-row">
                  <strong>Phone</strong>
                  <span>
                    {locationDetailModal.detail.phoneNumbers.primaryPhone}
                  </span>
                </div>
              )}
              {locationDetailModal.detail.websiteUri && (
                <div className="gmb-detail-row">
                  <strong>Website</strong>
                  <span>{locationDetailModal.detail.websiteUri}</span>
                </div>
              )}
              {locationDetailModal.detail.storefrontAddress && (
                <div className="gmb-detail-row">
                  <strong>Address</strong>
                  <span>
                    {[
                      ...(locationDetailModal.detail.storefrontAddress
                        .addressLines || []),
                      locationDetailModal.detail.storefrontAddress.locality,
                      locationDetailModal.detail.storefrontAddress
                        .administrativeArea,
                      locationDetailModal.detail.storefrontAddress.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
              {locationDetailModal.detail.categories?.primaryCategory && (
                <div className="gmb-detail-row">
                  <strong>Category</strong>
                  <span>
                    {
                      locationDetailModal.detail.categories.primaryCategory
                        .displayName
                    }
                  </span>
                </div>
              )}
              {locationDetailModal.detail.regularHours?.periods?.length > 0 && (
                <div className="gmb-detail-row">
                  <strong>Hours</strong>
                  <div>
                    {locationDetailModal.detail.regularHours.periods.map(
                      (p, i) => (
                        <div key={i} className="text-sm">
                          {p.openDay}: {p.openTime?.hours || 0}:
                          {String(p.openTime?.minutes || 0).padStart(2, "0")} -{" "}
                          {p.closeTime?.hours || 0}:
                          {String(p.closeTime?.minutes || 0).padStart(2, "0")}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ---------------- Reply Modal ---------------- */}
      <Modal
        show={replyModal.show}
        onHide={() => setReplyModal({ show: false, review: null, text: "" })}
        centered
        contentClassName="gmb-modal-content"
        className="gmb-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>Reply to Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            className="form-control"
            rows={4}
            value={replyModal.text}
            onChange={(e) =>
              setReplyModal({ ...replyModal, text: e.target.value })
            }
            placeholder="Write your reply..."
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary-600" onClick={submitReply}>
            Submit
          </button>
        </Modal.Footer>
      </Modal>

      {/* ---------------- Post Modal (create + edit) ---------------- */}
      <Modal
        show={postModal.show}
        onHide={() => setPostModal({ ...postModal, show: false })}
        centered
        size="lg"
        scrollable
        contentClassName="gmb-modal-content"
        className="gmb-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>
            {postModal.mode === "edit" ? "Edit Post" : "New Post"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Type tabs — locked once editing (Google doesn't allow type changes) */}
          <div className="gmb-segmented mb-16">
            {["STANDARD", "OFFER", "EVENT"].map((type) => (
              <button
                key={type}
                disabled={postModal.mode === "edit"}
                className={postModal.postType === type ? "active" : ""}
                onClick={() => setPostModal({ ...postModal, postType: type })}
              >
                {type === "STANDARD"
                  ? "Update"
                  : type === "OFFER"
                    ? "Offer"
                    : "Event"}
              </button>
            ))}
          </div>

          {/* Title — required for Event / Offer */}
          {postModal.postType !== "STANDARD" && (
            <>
              <label className="gmb-field-label">Title</label>
              <input
                type="text"
                maxLength={58}
                className="form-control mb-4"
                value={postModal.title}
                onChange={(e) =>
                  setPostModal({ ...postModal, title: e.target.value })
                }
                placeholder={
                  postModal.postType === "EVENT"
                    ? "e.g. Weekend Live Music"
                    : "e.g. 20% Off This Week"
                }
              />
              <p className="gmb-char-count">{postModal.title.length}/58</p>
            </>
          )}

          {/* Description */}
          <label className="gmb-field-label">Description</label>
          <textarea
            className="form-control mb-4"
            rows={4}
            maxLength={1500}
            value={postModal.summary}
            onChange={(e) =>
              setPostModal({ ...postModal, summary: e.target.value })
            }
            placeholder="Write your post..."
          />
          <p className="gmb-char-count">{postModal.summary.length}/1500</p>

          {/* Image */}
          <label className="gmb-field-label">Image URL</label>
          <input
            type="text"
            className="form-control mb-16"
            value={postModal.imageUrl}
            onChange={(e) =>
              setPostModal({ ...postModal, imageUrl: e.target.value })
            }
            placeholder="https://example.com/photo.jpg"
          />

          {/* Start / End date+time — Event & Offer only */}
          {postModal.postType !== "STANDARD" && (
            <>
              <div className="row mb-4">
                <div className="col-6">
                  <label className="gmb-field-label">Start date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={postModal.startDate}
                    onChange={(e) =>
                      setPostModal({ ...postModal, startDate: e.target.value })
                    }
                  />
                </div>
                <div className="col-6">
                  <label className="gmb-field-label">Start time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={postModal.startTime}
                    onChange={(e) =>
                      setPostModal({ ...postModal, startTime: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="row mb-16">
                <div className="col-6">
                  <label className="gmb-field-label">End date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={postModal.endDate}
                    onChange={(e) =>
                      setPostModal({ ...postModal, endDate: e.target.value })
                    }
                  />
                </div>
                <div className="col-6">
                  <label className="gmb-field-label">End time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={postModal.endTime}
                    onChange={(e) =>
                      setPostModal({ ...postModal, endTime: e.target.value })
                    }
                  />
                </div>
              </div>
            </>
          )}

          {/* Call to action — Update posts only */}
          {postModal.postType === "STANDARD" && (
            <>
              <label className="gmb-field-label">Button (call to action)</label>
              <div className="d-flex gap-8 mb-16">
                <select
                  className="form-control"
                  value={postModal.ctaType}
                  onChange={(e) =>
                    setPostModal({ ...postModal, ctaType: e.target.value })
                  }
                >
                  <option value="BOOK">Book</option>
                  <option value="ORDER">Order online</option>
                  <option value="SHOP">Buy</option>
                  <option value="LEARN_MORE">Learn more</option>
                  <option value="SIGN_UP">Sign up</option>
                  <option value="CALL">Call now</option>
                </select>
                <input
                  type="text"
                  className="form-control"
                  placeholder="https://..."
                  value={postModal.ctaUrl}
                  onChange={(e) =>
                    setPostModal({ ...postModal, ctaUrl: e.target.value })
                  }
                />
              </div>
            </>
          )}

          {/* Offer-specific extras */}
          {postModal.postType === "OFFER" && (
            <>
              <label className="gmb-field-label">Coupon code</label>
              <input
                type="text"
                className="form-control mb-12"
                value={postModal.couponCode}
                onChange={(e) =>
                  setPostModal({ ...postModal, couponCode: e.target.value })
                }
                placeholder="e.g. SAVE20"
              />

              <label className="gmb-field-label">Link to redeem offer</label>
              <input
                type="text"
                className="form-control mb-12"
                value={postModal.redeemUrl}
                onChange={(e) =>
                  setPostModal({ ...postModal, redeemUrl: e.target.value })
                }
                placeholder="https://..."
              />

              <label className="gmb-field-label">Terms and conditions</label>
              <textarea
                className="form-control mb-4"
                rows={2}
                value={postModal.termsConditions}
                onChange={(e) =>
                  setPostModal({
                    ...postModal,
                    termsConditions: e.target.value,
                  })
                }
                placeholder="e.g. Valid until stocks last. Cannot be combined with other offers."
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary-600" onClick={submitPost}>
            {postModal.mode === "edit" ? "Save Changes" : "Publish"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ---------------- Upload Media Modal ---------------- */}
      <Modal
        show={uploadModal.show}
        onHide={() => setUploadModal({ ...uploadModal, show: false })}
        centered
        contentClassName="gmb-modal-content"
        className="gmb-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>Upload Media</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="gmb-field-label">Photo or video</label>
          <label className="gmb-upload-drop">
            <input
              type="file"
              accept="image/*,video/*"
              onChange={onPickMediaFile}
            />
            {uploadModal.file
              ? uploadModal.file.name
              : "Click to choose a file from your device"}
            {uploadModal.previewUrl && (
              <img
                src={uploadModal.previewUrl}
                alt="preview"
                className="gmb-upload-preview"
              />
            )}
          </label>

          <label className="gmb-field-label mt-12">Category</label>
          <select
            className="form-control mb-12"
            value={uploadModal.category}
            onChange={(e) =>
              setUploadModal({ ...uploadModal, category: e.target.value })
            }
          >
            <option value="COVER">Cover</option>
            <option value="PROFILE">Profile</option>
            <option value="LOGO">Logo</option>
            <option value="EXTERIOR">Exterior</option>
            <option value="INTERIOR">Interior</option>
            <option value="PRODUCT">Product</option>
            <option value="ADDITIONAL">Additional</option>
          </select>
          <label className="gmb-field-label">Media Type</label>
          <select
            className="form-control"
            value={uploadModal.mediaFormat}
            onChange={(e) =>
              setUploadModal({ ...uploadModal, mediaFormat: e.target.value })
            }
          >
            <option value="PHOTO">Photo</option>
            <option value="VIDEO">Video</option>
          </select>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary-600" onClick={submitUpload}>
            Upload
          </button>
        </Modal.Footer>
      </Modal>

      <GMBProfileEditor
        show={profileEditorOpen}
        onHide={() => setProfileEditorOpen(false)}
        onSaved={() => {
          setProfileEditorOpen(false);
          viewLocationDetails();
        }}
      />
    </MasterLayout>
  );
};

export default GMBDashboard;
